import {AreaChart, BarChart, LineChart} from '@mantine/charts';
import type {ChartTooltipStylesNames} from '@mantine/charts';
import {Box} from 'src/design/atoms/Box';
import {useElementSize} from '@mantine/hooks';
import type {CSSProperties, FC} from 'react';
import {memo, useMemo} from 'react';
import type {AssistantChartResponse} from 'src/api/ProactApiClient';
import {AssistantChartTypeEnum} from 'src/api/ProactApiClient';
import {
  CHART_X_KEY,
  CHAT_CHART_COLORS,
  MIN_TOOLTIP_WIDTH,
  TICK_FONT_PX,
  TILTED_LABEL_ANGLE,
  chartDataRows,
  chartSeriesKey,
  chooseChartLayout,
  formatChartAxisTick,
  formatChartValue,
  truncateChartLabel,
} from './chatChartFormat';

const TOOLTIP_PROPS = {allowEscapeViewBox: {x: false, y: false}};

// Chat panes clip overflow, so the tooltip must fit inside the plot area instead of
// Mantine's 200px min-width with an unwrapped long label.
const tooltipStyles = (
  maxWidth: number
): Partial<Record<ChartTooltipStylesNames, CSSProperties>> => ({
  tooltip: {minWidth: Math.min(MIN_TOOLTIP_WIDTH, maxWidth), maxWidth},
  tooltipLabel: {
    padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-sm)',
    fontSize: 'var(--mantine-font-size-sm)',
    overflowWrap: 'break-word',
  },
  tooltipBody: {padding: '0 var(--mantine-spacing-sm) var(--mantine-spacing-xs)'},
  tooltipItem: {flexWrap: 'wrap', columnGap: 'var(--mantine-spacing-sm)'},
  tooltipItemBody: {marginInlineEnd: 0, minWidth: 0},
  tooltipItemName: {overflowWrap: 'break-word'},
  tooltipItemData: {whiteSpace: 'nowrap', marginInlineStart: 'auto'},
});

interface CartesianChartBodyProps {
  chart: AssistantChartResponse;
  availableWidth: number;
}

const CartesianChartBody = memo(function CartesianChartBody({
  chart,
  availableWidth,
}: CartesianChartBodyProps) {
  const data = useMemo(() => chartDataRows(chart), [chart]);
  const series = useMemo(
    () =>
      chart.series.map((s, index) => ({
        name: chartSeriesKey(index),
        label: s.label,
        color: CHAT_CHART_COLORS[index],
      })),
    [chart.series]
  );
  const layout = useMemo(
    () => chooseChartLayout(chart, availableWidth),
    [chart, availableWidth]
  );
  const styles = useMemo(() => tooltipStyles(layout.tooltipMaxWidth), [layout]);

  const valueAxisProps = {
    tickFormatter: (value: number) => formatChartAxisTick(value, chart.unit),
    allowDecimals: !layout.integerTicks,
  };
  const shared = {
    h: layout.height,
    w: '100%',
    data,
    dataKey: CHART_X_KEY,
    series,
    withLegend: series.length >= 2,
    withTooltip: true,
    valueFormatter: (value: number) => formatChartValue(value, chart.unit),
    styles,
    tooltipProps: TOOLTIP_PROPS,
  };
  const stacked = chart.type === AssistantChartTypeEnum.StackedBar;

  if (layout.kind === 'rows') {
    const barRows = {
      ...shared,
      orientation: 'vertical' as const,
      gridAxis: 'y' as const,
      tickLine: 'x' as const,
      xAxisProps: valueAxisProps,
      yAxisProps: {
        width: layout.categoryAxisWidth,
        interval: 0,
        // Recharts word-wraps a tick given a width; the label is already cut to one line.
        tick: {
          transform: 'translate(-10, 0)',
          fontSize: TICK_FONT_PX,
          fill: 'currentColor',
          width: undefined,
        },
        tickFormatter: (label: string) =>
          truncateChartLabel(label, layout.labelMaxChars),
      },
    };
    return stacked ? (
      <BarChart {...barRows} type="stacked" />
    ) : (
      <BarChart {...barRows} />
    );
  }

  const {tilt} = layout;
  const columns = {
    ...shared,
    gridAxis: 'x' as const,
    tickLine: 'y' as const,
    yAxisProps: valueAxisProps,
    xAxisProps: tilt
      ? {
          height: tilt.axisHeight,
          angle: TILTED_LABEL_ANGLE,
          tick: {
            angle: TILTED_LABEL_ANGLE,
            textAnchor: 'end' as const,
            fontSize: TICK_FONT_PX,
            fill: 'currentColor',
          },
          tickFormatter: (label: string) =>
            truncateChartLabel(label, tilt.labelMaxChars),
        }
      : undefined,
  };
  const margin = tilt ? {margin: {left: tilt.leftMargin}} : undefined;

  switch (chart.type) {
    case AssistantChartTypeEnum.Bar:
      return <BarChart {...columns} />;
    case AssistantChartTypeEnum.StackedBar:
      return <BarChart {...columns} type="stacked" />;
    case AssistantChartTypeEnum.Line:
      return (
        <LineChart
          {...columns}
          lineChartProps={margin}
          curveType="linear"
          strokeWidth={2}
        />
      );
    case AssistantChartTypeEnum.Area:
      return (
        <AreaChart {...columns} areaChartProps={margin} curveType="linear" />
      );
    default:
      return null;
  }
});

export interface ChatCartesianChartProps {
  chart: AssistantChartResponse;
}

/**
 * Bar, stacked-bar, line, or area Scout chart sized from its measured width.
 * @registry Cartesian Scout chart choosing horizontal bars, tilted labels, and tooltip width from the data.
 */
export const ChatCartesianChart: FC<ChatCartesianChartProps> = ({chart}) => {
  const {ref, width} = useElementSize();
  return (
    <Box ref={ref} w="100%">
      <CartesianChartBody chart={chart} availableWidth={width} />
    </Box>
  );
};
